import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { 
  CheckCircle, 
  Package, 
  Truck, 
  Mail, 
  ShoppingBag,
  ArrowRight,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import confetti from 'canvas-confetti';

const API = process.env.REACT_APP_BACKEND_URL;

const CheckoutSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [status, setStatus] = useState('loading');
  const [orderData, setOrderData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/shop');
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/api/payments/checkout-status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === 'paid') {
          setStatus('success');
          setOrderData(data);
          clearCart();
          
          // Celebration confetti!
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
          });
        } else if (data.status === 'expired') {
          setStatus('expired');
        } else if (pollCount < 5) {
          // Continue polling
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (pollCount < 5) {
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, pollCount, navigate, clearCart]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-16 w-16 text-[#5D6B55] animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#2C2824] mb-2">Processing Your Order</h2>
          <p className="text-[#6B6560]">Please wait while we confirm your payment...</p>
        </div>
      </div>
    );
  }

  if (status === 'expired' || status === 'error') {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-5xl">😕</span>
          </div>
          <h1 className="text-3xl font-bold text-[#2C2824] mb-4">
            {status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
          </h1>
          <p className="text-[#6B6560] mb-8">
            {status === 'expired' 
              ? 'Your payment session has expired. Please try again.'
              : 'We couldn\'t verify your payment. Please contact support if you were charged.'}
          </p>
          <Button 
            onClick={() => navigate('/cart')}
            className="bg-[#5D6B55] hover:bg-[#4A5745] text-white px-8 py-3"
          >
            Return to Cart
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Success Header */}
      <div className="bg-gradient-to-r from-[#5D6B55] to-[#4A5745] text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-14 w-14 text-[#5D6B55]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Thank You for Your Order!</h1>
          <p className="text-xl text-white/90">
            Your order has been confirmed and is being prepared
          </p>
          {orderData?.order_number && (
            <div className="mt-6 inline-block bg-white/20 rounded-lg px-6 py-3">
              <p className="text-sm text-white/80">Order Number</p>
              <p className="text-2xl font-bold">{orderData.order_number}</p>
            </div>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Order Timeline */}
          <div className="bg-white rounded-2xl shadow-sm border border-[#E8E4DE] p-8 mb-8">
            <h2 className="text-xl font-bold text-[#2C2824] mb-6">What's Next?</h2>
            
            <div className="space-y-6">
              <TimelineItem 
                icon={<CheckCircle className="h-5 w-5" />}
                title="Order Confirmed"
                description="We've received your order and payment"
                completed={true}
              />
              <TimelineItem 
                icon={<Mail className="h-5 w-5" />}
                title="Confirmation Email"
                description="A confirmation email has been sent to your inbox"
                completed={true}
              />
              <TimelineItem 
                icon={<Package className="h-5 w-5" />}
                title="Preparing Your Order"
                description="Our team is carefully packing your items"
                active={true}
              />
              <TimelineItem 
                icon={<Truck className="h-5 w-5" />}
                title="Shipping"
                description="Your order will be shipped within 1-2 business days"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="grid md:grid-cols-2 gap-4">
            <Button
              onClick={() => navigate('/account')}
              variant="outline"
              className="h-14 border-[#E8E4DE] text-[#2C2824] hover:bg-[#F5F1EB]"
            >
              <Package className="mr-2 h-5 w-5" />
              View My Orders
            </Button>
            <Button
              onClick={() => navigate('/shop')}
              className="h-14 bg-[#5D6B55] hover:bg-[#4A5745] text-white"
            >
              <ShoppingBag className="mr-2 h-5 w-5" />
              Continue Shopping
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>

          {/* Customer Support */}
          <div className="mt-8 text-center text-[#6B6560]">
            <p>
              Questions about your order?{' '}
              <button 
                onClick={() => navigate('/contact')}
                className="text-[#5D6B55] hover:text-[#4A5745] font-medium underline"
              >
                Contact our support team
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Timeline Item Component
const TimelineItem = ({ icon, title, description, completed, active }) => (
  <div className="flex gap-4">
    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
      ${completed ? 'bg-[#5D6B55] text-white' : 
        active ? 'bg-[#D4A574] text-white' : 
        'bg-[#E8E4DE] text-[#6B6560]'}`}
    >
      {icon}
    </div>
    <div className="flex-1 pb-6 border-l-2 border-[#E8E4DE] pl-6 -ml-5 mt-1">
      <h3 className={`font-semibold ${completed || active ? 'text-[#2C2824]' : 'text-[#6B6560]'}`}>
        {title}
      </h3>
      <p className="text-sm text-[#6B6560]">{description}</p>
    </div>
  </div>
);

export default CheckoutSuccessPage;
